import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import { FolderPlus, Pencil, X } from 'lucide-react';
import { EXPERIENCE_LEVEL_OPTIONS } from './interviewProfileUtils';

export default function ProfileFormModal({
  open,
  onClose,
  onSave,
  profile = null,
  saving = false,
  companies = [],
  defaultCompany = '',
}) {
  const isEditing = Boolean(profile?.id);

  const [form, setForm] = useState({
    name: '',
    target_role: '',
    experience_level: 'mid',
    description: '',
    company: '',
  });

  useEffect(() => {
    if (profile) {
      const compId = profile.company?.id || profile.company || '';
      setForm({
        name: profile.name || '',
        target_role: profile.target_role || '',
        experience_level: profile.experience_level || 'mid',
        description: profile.description || '',
        company: compId ? String(compId) : '',
      });
    } else {
      setForm({
        name: '',
        target_role: '',
        experience_level: 'mid',
        description: '',
        company: defaultCompany ? String(defaultCompany) : (companies.length > 0 ? String(companies[0].id) : ''),
      });
    }
  }, [profile, open, defaultCompany, companies]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    onSave(form);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
      <form onSubmit={handleSubmit}>
        <DialogTitle className="flex items-center justify-between border-b pb-3 font-bold text-gray-800">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-blue-50 text-blue-600 rounded-lg">
              {isEditing ? <Pencil className="w-5 h-5" /> : <FolderPlus className="w-5 h-5" />}
            </div>
            <span>{isEditing ? 'Edit Interview Profile' : 'Create New Interview Profile'}</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </DialogTitle>

        <DialogContent className="pt-4 space-y-4">
          {companies.length > 1 && (
            <FormControl fullWidth required sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}>
              <InputLabel id="company-label">Company</InputLabel>
              <Select
                labelId="company-label"
                label="Company"
                value={form.company || ''}
                onChange={(e) => setForm({ ...form, company: e.target.value })}
                disabled={isEditing}
                sx={{ borderRadius: '12px' }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      borderRadius: '12px',
                      boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
                      mt: 0.5,
                    },
                  },
                }}
              >
                {companies.map((c) => (
                  <MenuItem key={c.id} value={String(c.id)}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <TextField
            autoFocus
            required
            fullWidth
            label="Profile Name"
            placeholder="e.g. Frontend Developer (React / Next.js)"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            helperText="A descriptive template name visible to your recruitment team."
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TextField
              fullWidth
              label="Target Role"
              placeholder="e.g. Frontend Engineer"
              value={form.target_role}
              onChange={(e) => setForm({ ...form, target_role: e.target.value })}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
            />

            <FormControl fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}>
              <InputLabel id="experience-level-label">Experience Level</InputLabel>
              <Select
                labelId="experience-level-label"
                label="Experience Level"
                value={form.experience_level}
                onChange={(e) => setForm({ ...form, experience_level: e.target.value })}
                sx={{ borderRadius: '12px' }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      borderRadius: '12px',
                      boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
                      mt: 0.5,
                    },
                  },
                }}
              >
                {EXPERIENCE_LEVEL_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>

          <TextField
            fullWidth
            multiline
            rows={3}
            label="Description (Optional)"
            placeholder="Briefly describe what this interview profile tests and when to use it..."
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
          />
        </DialogContent>

        <DialogActions className="border-t px-6 py-3">
          <Button onClick={onClose} disabled={saving} sx={{ textTransform: 'none', fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={saving || !form.name.trim()}
            sx={{
              borderRadius: '10px',
              px: 3,
              textTransform: 'none',
              fontWeight: 700,
              background: 'linear-gradient(135deg, #2563eb 0%, #9333ea 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1d4ed8 0%, #7e22ce 100%)',
              },
            }}
          >
            {saving ? <CircularProgress size={20} color="inherit" /> : isEditing ? 'Save Changes' : 'Create Profile'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

